/**
 * Admin → Coupons.
 *
 * One page to mint coupon owners + coupon codes and see redemption
 * counts. Deliberately minimal — full UX is Phase-2 polish.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useLang } from '@/i18n/useLang';
import { adminApi } from '@/api/adminStorefront';
import type {
  CouponEntry,
  CreateCouponRequest,
  CreateOwnerRequest,
  DiscountType,
} from '@/api/adminStorefront';
import { AccountMenu } from '@/components/AccountMenu';

export function CouponsAdminPage() {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const ownersQ = useQuery({ queryKey: ['admin', 'owners'], queryFn: adminApi.listOwners });
  const couponsQ = useQuery({ queryKey: ['admin', 'coupons'], queryFn: adminApi.listCoupons });

  return (
    <div className="mx-auto max-w-screen-xl px-4 py-6 lg:px-8 lg:py-10">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <AdminBreadcrumb />
        <AccountMenu />
      </div>
      <h1 className="mb-2 text-2xl font-bold text-saffron-100">
        {t('कूपन प्रबंधन', 'Coupons admin')}
      </h1>
      <p className="mb-8 text-sm text-stone-400">
        {t(
          'कूपन-मालिक बनाएँ, कोड जारी करें, उपयोग देखें। यहीं रीसेलर ब्रांडिंग सेट करें (PDF white-labelling Phase 3 में आता है)।',
          'Create coupon owners, mint codes, watch redemptions. Reseller branding fields set here are forward-compat for the Phase-3 white-label PDF flow.',
        )}
      </p>

      <Section title={t('कूपन-मालिक', 'Coupon owners')}>
        <CreateOwnerForm onCreated={() => ownersQ.refetch()} />
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-stone-500">
              <tr className="border-b border-stone-800">
                <Th>{t('नाम', 'Name')}</Th>
                <Th>{t('संपर्क', 'Contact')}</Th>
                <Th>{t('रीसेलर?', 'Reseller?')}</Th>
                <Th>{t('रिडेम्प्शन', 'Redemptions')}</Th>
                <Th>{t('कुल वसूली (₹)', 'Net (₹)')}</Th>
              </tr>
            </thead>
            <tbody>
              {ownersQ.data?.map((o) => (
                <tr key={o.id} className="border-b border-stone-900/60">
                  <Td>{o.name}</Td>
                  <Td className="text-stone-400">
                    {o.contact_email ?? o.contact_phone ?? '—'}
                  </Td>
                  <Td>
                    {o.is_reseller
                      ? `${t('हाँ', 'yes')} (${o.reseller_share_pct}%)`
                      : t('नहीं', 'no')}
                  </Td>
                  <Td>{o.redemption_count}</Td>
                  <Td>{(o.net_paise / 100).toLocaleString()}</Td>
                </tr>
              ))}
              {ownersQ.data && ownersQ.data.length === 0 && (
                <tr><Td colSpan={5} className="text-stone-500">
                  {t('अभी कोई मालिक नहीं।', 'No owners yet.')}
                </Td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <div className="h-8" />

      <Section title={t('कूपन कोड', 'Coupon codes')}>
        <CreateCouponForm
          owners={ownersQ.data ?? []}
          onCreated={() => couponsQ.refetch()}
        />
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-stone-500">
              <tr className="border-b border-stone-800">
                <Th>{t('कोड', 'Code')}</Th>
                <Th>{t('मालिक', 'Owner')}</Th>
                <Th>{t('छूट', 'Discount')}</Th>
                <Th>{t('दायरा', 'Scope')}</Th>
                <Th>{t('उपयोग', 'Used')}</Th>
                <Th>{t('समाप्ति', 'Expires')}</Th>
                <Th>{t('स्थिति', 'Active')}</Th>
                <Th>{t('क्रिया', 'Actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {couponsQ.data?.map((c) => (
                <tr key={c.id} className="border-b border-stone-900/60">
                  <Td className="font-mono text-saffron-200">{c.code}</Td>
                  <Td>{c.owner_name}</Td>
                  <Td>{renderDiscount(c.discount_type, c.discount_value, lang)}</Td>
                  <Td>{c.scope === 'any' ? t('सभी रिपोर्ट', 'any report') : c.allowed_report_slugs?.join(', ')}</Td>
                  <Td>
                    {c.redemption_count}
                    {c.max_redemptions !== null ? ` / ${c.max_redemptions}` : ''}
                  </Td>
                  <Td>
                    <ExtendCouponExpiry coupon={c} onChanged={() => couponsQ.refetch()} />
                  </Td>
                  <Td>
                    <ToggleCouponActive coupon={c} onChanged={() => couponsQ.refetch()} />
                  </Td>
                  <Td>
                    <DeleteCouponButton coupon={c} onChanged={() => couponsQ.refetch()} />
                  </Td>
                </tr>
              ))}
              {couponsQ.data && couponsQ.data.length === 0 && (
                <tr><Td colSpan={8} className="text-stone-500">
                  {t('अभी कोई कूपन नहीं।', 'No coupons yet.')}
                </Td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

// ─── Forms ─────────────────────────────────────────────────────────────

function CreateOwnerForm({ onCreated }: { onCreated: () => void }) {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  const [body, setBody] = useState<CreateOwnerRequest>({
    name: '',
    contact_email: '',
    is_reseller: false,
    reseller_share_pct: 0,
  });
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => adminApi.createOwner({
      ...body,
      contact_email: body.contact_email || null,
    }),
    onSuccess: () => {
      setBody({ name: '', contact_email: '', is_reseller: false, reseller_share_pct: 0 });
      setErr(null);
      onCreated();
    },
    onError: (e) => setErr(String(e)),
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); void mut.mutateAsync(); }}
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Input label={t('नाम', 'Name')} value={body.name}
             onChange={(v) => setBody({ ...body, name: v })} required />
      <Input label={t('संपर्क ईमेल', 'Contact email')} value={body.contact_email ?? ''}
             onChange={(v) => setBody({ ...body, contact_email: v })} type="email" />
      <label className="flex items-end gap-2 text-xs text-stone-400">
        <input
          type="checkbox"
          checked={body.is_reseller ?? false}
          onChange={(e) => setBody({ ...body, is_reseller: e.target.checked })}
        />
        {t('रीसेलर (फ़ेज़ 3)', 'Reseller (Phase 3)')}
      </label>
      <Input label={t('रेव-शेयर %', 'Rev-share %')} type="number"
             value={String(body.reseller_share_pct ?? 0)}
             onChange={(v) => setBody({ ...body, reseller_share_pct: Number(v) || 0 })} />
      {body.is_reseller && (
        <>
          <Input label={t('ब्रांड नाम', 'Brand name')}
                 value={body.reseller_brand_name ?? ''}
                 onChange={(v) => setBody({ ...body, reseller_brand_name: v })} />
          <Input label={t('लोगो URL', 'Logo URL')}
                 value={body.reseller_logo_url ?? ''}
                 onChange={(v) => setBody({ ...body, reseller_logo_url: v })} />
        </>
      )}
      <div className="sm:col-span-2 lg:col-span-4">
        {err && <p className="mb-2 text-xs text-rose-300">{err}</p>}
        <button type="submit" disabled={!body.name.trim() || mut.isPending}
                className="rounded-md bg-saffron-600 px-3 py-2 text-sm font-semibold text-stone-950 hover:bg-saffron-500 disabled:cursor-not-allowed disabled:opacity-50">
          {t('मालिक जोड़ें', 'Add owner')}
        </button>
      </div>
    </form>
  );
}

function CreateCouponForm({ owners, onCreated }: {
  owners: Array<{ id: number; name: string }>;
  onCreated: () => void;
}) {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  const [body, setBody] = useState<CreateCouponRequest>({
    code: '',
    owner_id: owners[0]?.id ?? 0,
    discount_type: 'percent',
    discount_value: 10,
    scope: 'any',
  });
  const [err, setErr] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => adminApi.createCoupon(body),
    onSuccess: () => {
      setBody({ ...body, code: '' });
      setErr(null);
      onCreated();
    },
    onError: (e) => setErr(String(e)),
  });

  if (owners.length === 0) {
    return <p className="text-xs text-stone-500">
      {t('कूपन बनाने से पहले एक मालिक जोड़ें।', 'Add an owner before creating a coupon.')}
    </p>;
  }

  return (
    <form onSubmit={(e) => {
            e.preventDefault();
            if (!body.owner_id) setBody({ ...body, owner_id: owners[0].id });
            void mut.mutateAsync();
          }}
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <Input label={t('कोड', 'Code')} value={body.code}
             onChange={(v) => setBody({ ...body, code: v.toUpperCase() })} required />
      <Select label={t('मालिक', 'Owner')}
              value={String(body.owner_id)}
              onChange={(v) => setBody({ ...body, owner_id: Number(v) })}
              options={owners.map((o) => ({ value: String(o.id), label: o.name }))} />
      <Select label={t('छूट प्रकार', 'Discount type')}
              value={body.discount_type}
              onChange={(v) => setBody({ ...body, discount_type: v as DiscountType })}
              options={[
                { value: 'percent', label: t('% प्रतिशत', '% percent') },
                { value: 'flat', label: t('₹ निश्चित', '₹ flat') },
                { value: 'free', label: t('100% मुफ़्त', '100% free') },
              ]} />
      <Input label={t('मूल्य', 'Value')} type="number"
             value={String(body.discount_value)}
             onChange={(v) => setBody({ ...body, discount_value: Number(v) || 0 })} />
      <Input label={t('अधिकतम उपयोग', 'Max redemptions')} type="number"
             value={String(body.max_redemptions ?? '')}
             onChange={(v) => setBody({ ...body, max_redemptions: v ? Number(v) : null })} />
      <div className="sm:col-span-2 lg:col-span-5">
        {err && <p className="mb-2 text-xs text-rose-300">{err}</p>}
        <button type="submit" disabled={!body.code.trim() || mut.isPending}
                className="rounded-md bg-saffron-600 px-3 py-2 text-sm font-semibold text-stone-950 hover:bg-saffron-500 disabled:cursor-not-allowed disabled:opacity-50">
          {t('कूपन बनाएँ', 'Create coupon')}
        </button>
      </div>
    </form>
  );
}

function ToggleCouponActive({
  coupon,
  onChanged,
}: {
  coupon: { id: number; is_active: boolean };
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => adminApi.patchCoupon(coupon.id, { is_active: !coupon.is_active }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['admin', 'coupons'] }); onChanged(); },
  });
  return (
    <button type="button"
            onClick={() => void mut.mutateAsync()}
            className={`rounded-md px-2 py-1 text-xs font-semibold ring-1 ${
              coupon.is_active
                ? 'bg-emerald-950/40 text-emerald-200 ring-emerald-900'
                : 'bg-stone-800/40 text-stone-400 ring-stone-700'
            }`}>
      {coupon.is_active ? 'on' : 'off'}
    </button>
  );
}

function ExtendCouponExpiry({
  coupon,
  onChanged,
}: {
  coupon: CouponEntry;
  onChanged: () => void;
}) {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  const qc = useQueryClient();
  // ISO datetime → yyyy-MM-dd for <input type="date">
  const currentDate = coupon.expires_at ? coupon.expires_at.slice(0, 10) : '';
  const [pending, setPending] = useState(currentDate);

  const mut = useMutation({
    mutationFn: (value: string) =>
      adminApi.patchCoupon(coupon.id, {
        // FastAPI accepts an ISO 8601 string — pin to end-of-day UTC so the
        // coupon is valid for the entire chosen day in any timezone.
        expires_at: value ? `${value}T23:59:59Z` : null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'coupons'] });
      onChanged();
    },
  });

  const isDirty = pending !== currentDate;
  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="date"
        value={pending}
        onChange={(e) => setPending(e.target.value)}
        className="rounded-md border border-stone-700 bg-stone-950 px-1.5 py-0.5 text-xs text-stone-100 outline-none focus:border-saffron-500 [color-scheme:dark]"
        aria-label={t('समाप्ति तिथि', 'Expiry date')}
      />
      {isDirty && (
        <button
          type="button"
          onClick={() => void mut.mutateAsync(pending)}
          disabled={mut.isPending}
          className="rounded-md bg-saffron-600 px-2 py-0.5 text-xs font-semibold text-stone-950 hover:bg-saffron-500 disabled:opacity-50"
        >
          {mut.isPending ? '…' : t('लागू', 'save')}
        </button>
      )}
    </span>
  );
}

function DeleteCouponButton({
  coupon,
  onChanged,
}: {
  coupon: CouponEntry;
  onChanged: () => void;
}) {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => adminApi.deleteCoupon(coupon.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'coupons'] });
      onChanged();
    },
    onError: (e: unknown) => {
      const detail = (e as { detail?: string })?.detail ?? String(e);
      setErr(
        detail === 'coupon_has_redemptions'
          ? t(
              'पहले उपयोग हो चुका है — हटाने के बजाय "बंद" करें।',
              "Already redeemed — disable it instead (toggle Active off).",
            )
          : detail,
      );
    },
  });

  // Codes that have been redeemed can't be hard-deleted (audit trail).
  // Show a disabled button with a tooltip so admins see why.
  const blocked = (coupon.redemption_count ?? 0) > 0;

  return (
    <span className="inline-flex flex-col gap-0.5">
      <button
        type="button"
        disabled={blocked || mut.isPending}
        onClick={() => {
          const ok = window.confirm(
            t(
              `कूपन ${coupon.code} हटाएँ?`,
              `Delete coupon ${coupon.code}?`,
            ),
          );
          if (ok) void mut.mutateAsync();
        }}
        title={blocked ? t('रिडीम हो चुका है', 'Already redeemed') : ''}
        className="rounded-md border border-rose-900 px-2 py-1 text-xs text-rose-200 hover:bg-rose-950/40 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {t('हटाएँ', 'Delete')}
      </button>
      {err && <span className="text-[10px] text-rose-300">{err}</span>}
    </span>
  );
}

// ─── Primitive components ──────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl bg-stone-900/60 p-5 ring-1 ring-stone-800">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-saffron-400">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Input({
  label, value, onChange, type = 'text', required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <label className="block space-y-1 text-xs text-stone-400">
      <span>{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full rounded-md border border-stone-700 bg-stone-950 px-2 py-1.5 text-sm text-stone-100 outline-none focus:border-saffron-500 focus:ring-1 focus:ring-saffron-500"
      />
    </label>
  );
}

function Select({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label className="block space-y-1 text-xs text-stone-400">
      <span>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-stone-700 bg-stone-950 px-2 py-1.5 text-sm text-stone-100 outline-none focus:border-saffron-500 focus:ring-1 focus:ring-saffron-500"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider">{children}</th>;
}

function Td({ children, className, colSpan }: { children: React.ReactNode; className?: string; colSpan?: number }) {
  return <td colSpan={colSpan} className={`px-2 py-2 ${className ?? ''}`}>{children}</td>;
}

function renderDiscount(type: string, value: number, lang: 'hi' | 'en'): string {
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  if (type === 'percent') return `${value}% off`;
  if (type === 'flat') return `₹${(value / 100).toLocaleString()} off`;
  if (type === 'free') return t('100% मुफ़्त', '100% free');
  return type;
}

function AdminBreadcrumb() {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);
  return (
    <nav className="mb-4 flex items-center gap-2 text-xs text-stone-500">
      <Link to="/workbench" className="hover:text-saffron-200">
        {t('वर्कबेंच', 'Workbench')}
      </Link>
      <span>/</span>
      <Link to="/workbench/admin/orders" className="hover:text-saffron-200">
        {t('आदेश', 'Orders')}
      </Link>
      <span>·</span>
      <Link to="/workbench/admin/coupons" className="text-saffron-300">
        {t('कूपन', 'Coupons')}
      </Link>
    </nav>
  );
}
